import React from 'react';
import MindMap from '../../../components/features/minMapFeatures/mindMapFeatures';
import { useNoteState } from '../../../context/noteContexts/noteContext';
import { useColorContext } from '../../../context/colorContext';

const toSafeHex = (hex, fallback) => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const VisualNote = ({
	titles: titlesProp,
	mindMapsJson: mindMapsJsonProp,
	className = '',
	titleClassName = '',
	sectionClassName = '',
}) => {
	const { visualMindMapTitles, visualMindMapsJson } = useNoteState();
	const {
		bgPrimary,
		bgSecondary,
		textPrimary,
		textSecondary,
		activeAccent,
	} = useColorContext();

	const safeBgPrimary = toSafeHex(bgPrimary, '#0F1115');
	const safeBgSecondary = toSafeHex(bgSecondary, '#181C22');
	const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
	const safeTextSecondary = toSafeHex(textSecondary, '#BDBDBD');
	const safeAccent = toSafeHex(activeAccent, '#3B82F6');
	const safeMenuBg = toSafeHex(bgSecondary, '#181C22');
	const safeChipBg = hexToRgba(safeTextPrimary, 0.12, safeMenuBg);
	const nodeSurfaceBase = '#050505';

	const titles = Array.isArray(titlesProp) ? titlesProp : (Array.isArray(visualMindMapTitles) ? visualMindMapTitles : []);
	const mindMapsJson = Array.isArray(mindMapsJsonProp) ? mindMapsJsonProp : (Array.isArray(visualMindMapsJson) ? visualMindMapsJson : []);
	const itemCount = Math.max(titles.length, mindMapsJson.length);

	if (itemCount === 0) {
		return (
			<div
				className={className}
				style={{
					maxWidth: 1500,
					margin: '0 auto',
					padding: '22px 8px 8px',
				}}
			>
				<p style={{ color: safeTextSecondary }}>No mindmap available.</p>
			</div>
		);
	}

	return (
		<div
			className={className}
			style={{
				maxWidth: 1500,
				margin: '0 auto',
				display: 'flex',
				flexDirection: 'column',
				gap: 32,
				padding: '2px 12px 20px',
			}}
		>
			{Array.from({ length: itemCount }).map((_, index) => {
				const title = titles[index] || `Mindmap ${index + 1}`;
				const mindMapData = mindMapsJson[index];
				
				const sectionBg = safeMenuBg;
				
				const sectionStyle = {
					padding: '32px 28px 28px',
					borderRadius: 28,
					marginBottom: 0,
					background: sectionBg,
					backdropFilter: 'blur(8px)',
					WebkitBackdropFilter: 'blur(8px)',
					border: 'none',
					boxShadow: 'none',
					position: 'relative',
					overflow: 'hidden',
				};
				
				// Premium title styling
				const titleStyle = {
					margin: 0,
					fontSize: '1.32rem',
					fontWeight: 780,
					letterSpacing: '-0.01em',
					color: safeTextPrimary,
					padding: 0,
					textShadow: 'none',
					background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${safeTextSecondary} 100%)`,
					WebkitBackgroundClip: 'text',
					WebkitTextFillColor: 'transparent',
					backgroundClip: 'text',
				};
				
				const cardHeaderStyle = {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 16,
					marginBottom: 22,
					padding: '4px 4px 0',
				};
				
				// Neutral premium chip styling
				const chipStyle = {
					padding: '8px 16px',
					borderRadius: 999,
					fontSize: '0.75rem',
					fontWeight: 720,
					letterSpacing: '0.08em',
					textTransform: 'uppercase',
					color: hexToRgba(safeTextPrimary, 0.95),
					background: `linear-gradient(135deg, ${hexToRgba(safeTextPrimary, 0.12)} 0%, ${hexToRgba(safeTextPrimary, 0.06)} 100%)`,
					boxShadow: 'none',
					whiteSpace: 'nowrap',
					backdropFilter: 'blur(10px)',
					WebkitBackdropFilter: 'blur(10px)',
					fontVariantNumeric: 'tabular-nums',
				};

				if (!mindMapData) {
					return (
						<section key={`visual-note-empty-${index}`} className={sectionClassName} style={sectionStyle}>
							<div style={cardHeaderStyle}>
								<h3 className={titleClassName} style={titleStyle}>{title}</h3>
								<span style={chipStyle}>Section {index + 1}</span>
							</div>
							<p style={{ color: safeTextSecondary, fontSize: '0.95rem' }}>No mindmap data for this title.</p>
						</section>
					);
				}

				return (
					<section key={`visual-note-${index}`} className={sectionClassName} style={sectionStyle}>
						<div style={cardHeaderStyle}>
							<h3 className={titleClassName} style={titleStyle}>{title}</h3>
							<span style={chipStyle}>Section {index + 1}</span>
						</div>
						<MindMap
							data={mindMapData}
							blockTextColor={safeTextPrimary}
							actionButtonTextColor={safeTextPrimary}
							blockBackgroundColor={safeChipBg}
							actionButtonBackgroundColor={safeChipBg}
							blockBorderColor={'transparent'}
							actionButtonBorderColor={'transparent'}
							canvasBackground={safeBgPrimary}
							canvasBackdropColor={safeBgPrimary}
							canvasBorder={'transparent'}
							canvasShadow={'none'}
							traitColor={hexToRgba(safeAccent, 0.82)}
							fullscreenButtonColor={safeAccent}
							zoomButtonColor={safeAccent}
							expandButtonBackgroundColor={safeChipBg}
							expandButtonHoverBackgroundColor={safeChipBg}
						/>
					</section>
				);
			})}
		</div>
	);
};

export default VisualNote;